import { Component, inject, input, OnInit, ViewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { cloneDeep, intersectionWith, isEqual, sortBy, union } from 'lodash';
import { RangeService } from '../../../../../shared/services/range.service';
import {
  ExtensionRangesDisplayFormComponent,
  RangeDisplayFormModel,
} from '../../../../../forms/fields/extension-ranges/extension-ranges-display-form/extension-ranges-display-form.component';
import { ZiroCustomSelectConfig } from '../../../../../shared/custom-configs/custom-select/ziro-custom-select.component';
import { ValidationTarget } from '../dn-ranges/dial-plan-management-dn-ranges-form.component';
import { TranslateService } from '@ngx-translate/core';
import {
  CucmMetadata,
  CustomMultiSelect,
  CustomSelect,
  DialPlanManagementGroup,
  DnDidRange,
  SiteSummary,
} from '../../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import {
  ZiroFormConfig,
  ZiroFormsAutogenerationConfig,
  ZiroFormsUpdate,
  ZiroFormsValidationConfig,
  ZiroFormsValidationState,
} from '../../../../../forms/ziro-forms-models';
import { ExtensionRangesOptionalValidators } from '../../../../../forms/fields/extension-ranges/ziro-extension-range-models';
import { ZiroTextareaConfig } from '../../../../../forms/fields/textarea/ziro-textarea.component';
import { ButtonStyles } from '../../../../../button/button.component';

export interface CiscoDidRangesFormEntity {
  route_partition: CustomSelect;
  ranges: DnDidRange[];
}

export interface CiscoDidRangesFormData {
  ranges: DnDidRange[];
  bulkRanges: '';
  route_partition: CustomSelect;
}

@Component({
  selector: 'ziro-cisco-dial-plan-management-did-ranges-form',
  templateUrl: './dial-plan-management-did-ranges-form.component.html',
  providers: [],
  standalone: false,
})
export class CiscoDialPlanManagementDidRangesFormComponent
  extends ZiroFormAbstractDirective<CiscoDidRangesFormEntity, CiscoDidRangesFormData>
  implements OnInit
{
  private _translateService = inject(TranslateService);

  readonly cucmMetadata = input<CucmMetadata>();
  readonly groupId = input<number>();
  readonly clusterId = input<number>();
  readonly dialPlanGroups = input<DialPlanManagementGroup[]>([]);
  readonly siteSummary = input<SiteSummary>();

  @ViewChild(ExtensionRangesDisplayFormComponent) extensionRangesDisplayForm: ExtensionRangesDisplayFormComponent;

  validationStates = ZiroFormsValidationState;
  formConfig: ZiroFormConfig;
  displayRangeFormEntity: RangeDisplayFormModel;
  displayRangeForm = false;
  extensionRangesDisplayFormValidationState: ZiroFormsValidationState;
  displayRangeFormOptionalValidators: ExtensionRangesOptionalValidators;

  ngOnInit(): void {
    this.formConfig = {
      fields: {
        route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
          dataAutomation: 'did-ranges-route-partition',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
            displayValues: sortBy(this.cucmMetadata().routePartitions),
            availableOptions: sortBy(this.cucmMetadata().routePartitions),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation(
            'INVALID',
            'route_partition',
            this.cucmMetadata().routePartitions,
          ),
          autogeneration: this._getCustomSelectAutogeneration('route_partition', this.cucmMetadata().routePartitions),
        },
        bulkRanges: {
          label: 'tkey;dialplanmanagement.admin.group.generate.didranges',
          helpText: () => 'tkey;dialplanmanagement.admin.group.generate.didranges.helpblock',
          dataAutomation: 'did-ranges-bulk-ranges',
          componentConfig: new ZiroTextareaConfig(),
          validation: [
            {
              validator: (val: string) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }

                const split = val.split(',');
                const hasInvalid = split.some((v: string) => {
                  return !/^\+?[0-9]+(?:-\+?[0-9]+)*$/.test(v.trim());
                });

                return hasInvalid ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;rangeDirective.range.unsupportedpattern',
            },
          ],
        },
      },
    };

    this.displayRangeFormOptionalValidators = {
      overlappingRanges: true,
      strictE164Validation: false,
      hasSameLengthValidation: true,
      maxDiffValidation: 99999,
      minLengthValidation: 1,
      maxLengthValidation: 24,
      overlappingRangesInOtherCiscoDialPlanGroups: {
        clusterId: this.clusterId(),
        dialPlanGroups: !this.groupId()
          ? this.dialPlanGroups()
          : this.dialPlanGroups().filter((group: DialPlanManagementGroup) => group.id !== this.groupId().toString()),
        range: 'DID',
      },
    };

    this.displayRangeFormEntity = { ranges: cloneDeep(this.entity.ranges) };
    this.displayRangeForm = true;
  }

  generateBulkRangesClicked(): void {
    if (!this.formData['bulkRanges'].trim()) {
      return;
    }

    this.displayRangeForm = false;

    // Remove new lines, spaces
    const generatedRanges = RangeService.generateRangesFromArray(
      this.formData['bulkRanges'].replace(/\n|\r/g, '').replace(/ /g, '').split(','),
    );
    const currentRanges = cloneDeep(this.entity.ranges).filter((r: DnDidRange) => r.start && r.end);
    const updatedRanges = [...currentRanges, ...generatedRanges];
    const newEntity = {
      ...this.entity,
      ranges: updatedRanges,
    };

    this.formData['bulkRanges'] = '';
    this.entitySource.next(cloneDeep(newEntity));
    this.displayRangeFormEntity = { ranges: newEntity.ranges };

    setTimeout(() => {
      this.displayRangeForm = true;
    });
  }

  submit(): Observable<null> {
    this.setDisplayFormValidationState();
    return of(null);
  }

  toFormData = (entity: CiscoDidRangesFormEntity): CiscoDidRangesFormData => {
    return {
      route_partition: {
        name: entity.route_partition?.name,
        defaultOption: entity.route_partition?.defaultOption,
        possibleOptions: entity.route_partition?.possibleOptions,
        required: entity.route_partition?.required,
        show: entity.route_partition?.show,
      },
      ranges: entity.ranges,
      bulkRanges: '',
    };
  };

  toEntity = (formData: CiscoDidRangesFormData): CiscoDidRangesFormEntity => {
    return {
      route_partition: {
        name: formData.route_partition.name,
        defaultOption: formData.route_partition.defaultOption,
        possibleOptions: formData.route_partition.possibleOptions,
        required: formData.route_partition.required,
        show: formData.route_partition.show,
      },
      ranges: formData.ranges,
    };
  };

  setDisplayFormValidationState(): void {
    this.extensionRangesDisplayFormValidationState =
      this.isFormSubmitted || this.extensionRangesDisplayForm.extensionRanges.showValidation
        ? this.extensionRangesDisplayForm.validationState
        : this.validationStates.VALID;
  }

  onRangeFormUpdate(data: ZiroFormsUpdate<RangeDisplayFormModel>): void {
    // Always update validation state, don't always update entity. Async val will fire this multiple times so entity
    // won't necessarily change onFormUpdate
    if (isEqual(this.entity.ranges, data.new.ranges)) {
      this.setDisplayFormValidationState();
    } else {
      const newEntity = {
        ...cloneDeep(this.entity),
        ranges: data.new.ranges,
      };

      this.entitySource.next(newEntity);

      this.setDisplayFormValidationState();
    }
  }

  private _getSelectOptions(
    formValues: string[],
    validationTarget: ValidationTarget,
    availableOptions: string[],
  ): string[] {
    return formValues.filter((val: string) => {
      const validationState = this._invalidOptionValidator(val, availableOptions);

      return validationTarget === 'VALID'
        ? validationState === ZiroFormsValidationState.VALID
        : validationState === ZiroFormsValidationState.INVALID;
    });
  }

  private _invalidOptionValidator(val: string, availableOptions: string[]): ZiroFormsValidationState {
    return val && !availableOptions.includes(val) ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
  }

  private _getCustomSelectAutogeneration(
    fieldId: keyof CiscoDidRangesFormData,
    availableOptions: string[],
  ): ZiroFormsAutogenerationConfig {
    return {
      linkLabel: 'tkey;shared.html.customizable_fields.text_input.fix_it.label',
      generateValue: (fieldValue) => {
        const validationStateSingleSelect =
          this._invalidOptionValidator((this.formData[fieldId] as CustomSelect).defaultOption, availableOptions) ===
          ZiroFormsValidationState.VALID;

        const defaultOption = validationStateSingleSelect ? (this.formData[fieldId] as CustomSelect).defaultOption : '';

        const select = this.formData[fieldId] as CustomSelect;

        const possibleOptions = this._getSelectOptions(
          (this.formData[fieldId] as CustomSelect | CustomMultiSelect).possibleOptions,
          'VALID',
          availableOptions,
        );

        const updatedSelect: CustomSelect = {
          ...select,
          defaultOption,
          possibleOptions: intersectionWith(possibleOptions, availableOptions),
        };
        this.entitySource.next({
          ...this.formData,
          [fieldId]: updatedSelect,
        });

        return fieldValue;
      },
      hidden: () => {
        const invalidPossibleOptions = this._getSelectOptions(
          (this.formData[fieldId] as CustomSelect | CustomMultiSelect).possibleOptions,
          'INVALID',
          availableOptions,
        );
        const validationState = this._invalidOptionValidator(
          (this.formData[fieldId] as CustomSelect).defaultOption,
          availableOptions,
        );

        return validationState !== ZiroFormsValidationState.INVALID && invalidPossibleOptions.length === 0;
      },
      inline: true,
    };
  }

  private _getCustomSelectValidation(
    validationTarget: ValidationTarget,
    fieldId: keyof CiscoDidRangesFormData,
    availableOptions: string[],
  ): ZiroFormsValidationConfig {
    return [
      {
        validator: (val: CustomSelect) => {
          if (val && val.required && !val.defaultOption) {
            return ZiroFormsValidationState.INVALID;
          }
          return ZiroFormsValidationState.VALID;
        },
        message: 'tkey;custom_select.required.all_defaults_required.error',
      },
      {
        validator: (select: CustomSelect | CustomMultiSelect) => {
          let formValues;
          if ('defaultOptions' in select) {
            formValues = union([...select.defaultOptions, ...select.possibleOptions]);
          } else {
            formValues = union([...select.possibleOptions, select.defaultOption]);
          }

          return this._getSelectOptions(formValues, 'INVALID', availableOptions).length
            ? ZiroFormsValidationState.INVALID
            : ZiroFormsValidationState.VALID;
        },
        message: () => {
          const formValues = union([
            ...(this.formData[fieldId] as CustomSelect).possibleOptions,
            (this.formData[fieldId] as CustomSelect).defaultOption,
          ]);

          const invalidOptions = this._getSelectOptions(formValues, validationTarget, availableOptions);

          const translation = this._translateService.instant('tkey;select.invalid_options.label');

          return `${translation} ${invalidOptions.join(', ')}`;
        },
      },
      {
        validator: (val: CustomSelect) => {
          if (!val.show && val.possibleOptions.length > 1) {
            return ZiroFormsValidationState.WARNING;
          }
          return ZiroFormsValidationState.VALID;
        },
        message: 'tkey;custom_select.show.multiple_values.warning',
      },
    ];
  }

  protected readonly ButtonStyles = ButtonStyles;
}
